<script setup lang="ts">
import { defineAsyncComponent, nextTick, ref, shallowRef } from 'vue';
import LoadingSpinner from './loading-spinner.vue';
import model from '@/ts/model';
import type { Component } from "vue";
import globalImport from '@/ts/utils/global-import.ts';
import { getApp } from '@/ts/management/app-management.ts';

interface AsyncLoadProps {
	load: () => Promise<{ default: Component } | Component>,
	bindings?: Record<string, unknown>,
	global?: boolean,
	name?: string,
	onLoad?: () => void,
};

const props = defineProps<AsyncLoadProps>();

/*
	Views are loaded asynchronously to keep the initial bundle size small.
	Once a view has been loaded, it is registered globally via app.component().
	This ensures that when navigating back in browser history, the component
	mounts immediately and the browser can correctly restore the previous
	scroll position.

	Components are registered globally if the "name" property is passed. 
*/
const customName = props.name;

const asyncComp = shallowRef<Component>();
const loaded = ref<boolean>(customName ? model.loadedComponents.includes(customName) : false);

if(customName){
	const onLoad = props.onLoad;
	if(!loaded.value){
		globalImport(props.load, props.global).then(resp => {
			getApp()?.component(customName, ('default' in resp) ? resp.default : resp);
			model.loadedComponents.push(customName);
			loaded.value = true;
			const onLoad = props.onLoad;
			if(onLoad) nextTick(() => onLoad());
		});
	}
	else if(onLoad) nextTick(() => onLoad());
}
else{
	asyncComp.value = defineAsyncComponent({
	  loader: retryUntilOnline(props.load),
	  loadingComponent: props.global ? undefined : LoadingSpinner,
	  delay: 0,
	});
	
	function retryUntilOnline<T>(factory: () => Promise<T>, delay = 2000) {
	  return () =>
		new Promise<T>((resolve) => {
			const attempt = () => {
				props.global && model.globalRequests++;
				factory()
				.then(resolve)
				.catch(() => {
					setTimeout(attempt, delay);
				}).finally(() => {
					if(props.global) model.globalRequests--;
				});
			};
			attempt();
		});
	}
}

</script>

<template>
	<template v-if="name">
		<template v-if="loaded">
			<component :is="name" v-bind="bindings" />
		</template>
		<template v-else>
			<loading-spinner :max-height="true" :avoid-header="true" />
		</template>
	</template>
	<template v-else>
		<component :is="asyncComp" v-bind="bindings" />
	</template>
</template>